import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

import { UiModule } from 'src/app/ui/ui.module';

import {
  IPriceColumnMapping,
  IPriceImportInspection,
  PriceColumnRole,
  PriceImportAudience,
} from '../../models/price-observation.models';

/**
 * The fields the reader can be told about, in the order the screen asks them.
 * `product_code` and `price` carry the two hard guards; the rest are optional.
 *
 * The server sends the same list (`inspection.roles`) and it wins when it is
 * there: the fields a reading knows about are the server's business, and two
 * lists drifting apart would offer a select nothing reads.
 */
const ROLES: PriceColumnRole[] = [
  'product_code',
  'price',
  'product_name',
  'customer',
  'price_date',
  'valid_until',
  'customer_code',
  'unit',
  'currency',
];

/** Fields without which no observation can be produced. */
const REQUIRED_ROLES: PriceColumnRole[] = ['product_code', 'price'];

/**
 * Where the operator says which column holds each field.
 *
 * Nothing is pre-filled and the sample recomputes on every change: attributing
 * rows without the operator confirming what each column was is how a file gets
 * read wrong. The operator sees the consequence of the select they just moved,
 * on their own data, before anything is created.
 *
 * It asks per field ("the price is in which column?"), not per column: the
 * operator knows their file has a price, whereas classifying eleven columns
 * into a vocabulary of ours is our question, not theirs. It also bounds the
 * mistakes possible — a field cannot claim two columns by construction, and the
 * mirror mistake (two fields on one column) is the guard below.
 *
 * The guards live on the backend, where every other caller is held to them too.
 * They are mirrored here only to disable the button and say why.
 */
@Component({
  selector: 'app-price-column-mapping-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSelectModule, TranslateModule, UiModule],
  templateUrl: './price-column-mapping-step.component.html',
  styleUrls: ['./price-column-mapping-step.component.scss'],
})
export class PriceColumnMappingStepComponent {
  /**
   * The refusal itself. One sheet, because the server reads the first readable
   * sheet of a workbook and nothing else — a tab strip here would offer to map
   * a sheet the import would never read.
   */
  @Input() set inspection(value: IPriceImportInspection | null) {
    this._inspection = value;
    this.roles = value?.roles?.length ? value.roles : ROLES;
    this.labels = value?.labels ?? [];
    this.columns = this.labels.map((label, index) => ({ index, label }));
    this.sampleSize = value?.sample?.length ?? 0;
    this.reset();
  }
  get inspection(): IPriceImportInspection | null {
    return this._inspection;
  }

  /** The audience chosen at the drop step — what answers "no customer
   * column". */
  @Input() scope: PriceImportAudience | null = null;

  /**
   * The audience is settled elsewhere, so the customer-column guard does not
   * apply. Set by the journal's mapping dialog: the tariff arrived inside a
   * message and the message decided its audience — decided, but not one of the
   * four an upload can state, which is why it is not a `scope` value.
   */
  @Input() audienceDecided = false;

  @Output() confirmed = new EventEmitter<IPriceColumnMapping>();
  @Output() cancelled = new EventEmitter<void>();

  /** One column index per field; a field the operator left alone is absent. */
  columnOf: Partial<Record<PriceColumnRole, number>> = {};

  // ── What is on screen ─────────────────────────────────────────────────────
  //
  // Everything below is derived once per change, not per change-detection
  // cycle: bound to a getter, each tick would hand `*ngFor` freshly allocated
  // objects to diff and rebuild the whole sample table.

  roles: PriceColumnRole[] = ROLES;
  labels: string[] = [];
  /** The columns of the file, as the options of every select. */
  columns: { index: number; label: string }[] = [];
  sampleSize = 0;

  /**
   * The live sample: the body rows, rendered through the current mapping.
   * Recomputed on every change on purpose — a preview lagging behind the
   * selects would show the answer to the previous question.
   *
   * Only the mapped columns are shown, in the order the fields are asked: the
   * table is the reading, and a column nobody named is not part of it.
   */
  sampleColumns: { role: PriceColumnRole; index: number }[] = [];
  sampleRows: { cells: string[] }[] = [];

  /**
   * Two fields on one column. Nothing would fail: the reader would read that
   * cell as both and publish a price called "SKU-1".
   */
  duplicateColumn: { column: string; roles: PriceColumnRole[] } | null = null;
  blockedReasonParams: Record<string, string> = {};

  private duplicatedRoles = new Set<PriceColumnRole>();
  private _inspection: IPriceImportInspection | null = null;

  isRequired(role: PriceColumnRole): boolean {
    return REQUIRED_ROLES.includes(role);
  }

  /**
   * How many sample rows carry a customer — information, not a gate.
   *
   * Only meaningful once a customer column is named: it counts the rows whose
   * cell in that column is filled. With no column mapped it answers zero, not
   * `sampleSize` — otherwise the screen claims "2/2 rows carry a customer" on a
   * file where nothing names a customer, beside a button blocked for that.
   */
  get hasCustomerColumn(): boolean {
    return this.columnOf['customer'] != null;
  }

  get resolvedCustomerCount(): number {
    const index = this.columnOf['customer'];
    if (index == null) return 0;
    return (this._inspection?.sample ?? []).filter(
      (row) => (row[index] ?? '').trim().length > 0,
    ).length;
  }

  // ── The guards, mirrored to explain the disabled button ──────────────────

  get missingPrice(): boolean {
    return this.columnOf['price'] == null;
  }

  get missingProduct(): boolean {
    return this.columnOf['product_code'] == null && this.columnOf['product_name'] == null;
  }

  /**
   * No customer column means the audience chosen at the drop step applies to
   * every row, so there has to be one. `file` is not an audience — it is the
   * deferral to a column this mapping does not name.
   */
  get missingAudience(): boolean {
    if (this.columnOf['customer'] != null || this.audienceDecided) return false;
    return !this.scope || this.scope === 'file';
  }

  isDuplicated(role: PriceColumnRole): boolean {
    return this.duplicatedRoles.has(role);
  }

  get blockedReasonKey(): string | null {
    if (this.missingPrice) return 'knowledge_base.import.mapping.blocked_no_price';
    if (this.missingProduct) return 'knowledge_base.import.mapping.blocked_no_product';
    if (this.duplicateColumn) return 'knowledge_base.import.mapping.blocked_duplicate_column';
    if (this.missingAudience) return 'knowledge_base.import.mapping.blocked_no_audience';
    return null;
  }

  get canConfirm(): boolean {
    return this.labels.length > 0 && this.blockedReasonKey === null;
  }

  // ── Interaction ─────────────────────────────────────────────────────────

  /** `null` clears the field: not answering is a legitimate answer. */
  setColumn(role: PriceColumnRole, index: number | null): void {
    const next = { ...this.columnOf };
    if (index == null) {
      delete next[role];
    } else {
      next[role] = index;
    }
    this.columnOf = next;
    this.deriveFromMapping();
  }

  roleLabelKey(role: PriceColumnRole): string {
    return `knowledge_base.import.mapping.role.${role}`;
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.confirmed.emit({
      fields: { ...this.columnOf },
      header_row: this._inspection?.header_row ?? 0,
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByRole(_index: number, role: PriceColumnRole): string {
    return role;
  }

  /** A new file is a new question: nothing carries over from the previous one. */
  private reset(): void {
    this.columnOf = {};
    this.deriveFromMapping();
  }

  /** Everything the mapping decides, computed once per edit. */
  private deriveFromMapping(): void {
    this.sampleColumns = this.roles
      .filter((role) => this.columnOf[role] != null)
      .map((role) => ({ role, index: this.columnOf[role] as number }));
    this.sampleRows = (this._inspection?.sample ?? []).map((row) => ({
      cells: this.sampleColumns.map(({ index }) => row[index] ?? ''),
    }));

    const seen = new Map<number, PriceColumnRole[]>();
    for (const role of this.roles) {
      const index = this.columnOf[role];
      if (index == null) continue;
      seen.set(index, [...(seen.get(index) ?? []), role]);
    }
    this.duplicateColumn = null;
    for (const [index, roles] of seen) {
      if (roles.length > 1) {
        this.duplicateColumn = { column: this.labels[index] ?? `#${index + 1}`, roles };
        break;
      }
    }
    this.duplicatedRoles = new Set(this.duplicateColumn?.roles ?? []);
    this.blockedReasonParams = this.duplicateColumn
      ? { column: this.duplicateColumn.column }
      : {};
  }
}
